import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell, Search, Send, TestTube2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import ConfirmModal from '../../components/ConfirmModal';
import {
  TITEL_MAX,
  TEKST_MAX,
  broadcastFoutTekst,
  filterSamenvatting,
  formatMoment,
  maakIdempotencyKey,
  resultaatRegels,
  valideerBericht,
  verstuurBroadcast,
} from '../../utils/pushBroadcastUtils';

const PAGINA_GROOTTE = 20;

// ════════════════════════════════════════════════════════════════
// Coachpagina "Push notificaties" (/coach/push).
//
// Twee tabs: een nieuw bericht opstellen (met ontvangerscontrole,
// testpush en telefoonpreview) en de verzendgeschiedenis.
//
// Harde uitgangspunten:
//  • de browser krijgt nooit een pushtoken te zien — get_broadcast_recipients
//    geeft uitsluitend een bereikbaarheidsstatus terug;
//  • verzenden gebeurt via één server-side aanroep (send-broadcast-push) die
//    de doelgroep opnieuw valideert, de daglimiet atomisch claimt en per
//    Expo-ticket rapporteert;
//  • de bevestiging werkt op een bevroren momentopname, zodat gewijzigde
//    filters de doelgroep niet meer onder de knop vandaan kunnen wisselen.
// ════════════════════════════════════════════════════════════════
export default function BroadcastPushPage() {
  const { role } = useAuth();
  const { showSuccess, showError } = useToast();

  const isAdminOrSuper = role === 'admin_coach' || role === 'superadmin';

  const [actieveTab, setActieveTab] = useState('nieuw');

  // ── Filters ────────────────────────────────────────────────────
  const [locationId, setLocationId] = useState('');
  const [membershipTypeId, setMembershipTypeId] = useState('');
  const [coachId, setCoachId] = useState('');

  // ── Keuzelijsten (elk met eigen foutstate) ─────────────────────
  const [locations, setLocations] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [optieFouten, setOptieFouten] = useState({ locaties: '', abonnementen: '', coaches: '' });

  // ── Ontvangers ─────────────────────────────────────────────────
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [recipientsError, setRecipientsError] = useState('');
  const [geselecteerd, setGeselecteerd] = useState(() => new Set());
  const [zoek, setZoek] = useState('');

  // ── Bericht ────────────────────────────────────────────────────
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  // ── Daglimiet ──────────────────────────────────────────────────
  const [limiet, setLimiet] = useState(null);
  const [limietFout, setLimietFout] = useState('');

  // ── Verzenden ──────────────────────────────────────────────────
  const [bevestiging, setBevestiging] = useState(null); // bevroren momentopname
  const [verzendBezig, setVerzendBezig] = useState(false);
  const [testBezig, setTestBezig] = useState(false);
  const [laatsteResultaat, setLaatsteResultaat] = useState(null);

  // ── Geschiedenis ───────────────────────────────────────────────
  const [historie, setHistorie] = useState([]);
  const [historieTotaal, setHistorieTotaal] = useState(0);
  const [historiePagina, setHistoriePagina] = useState(0);
  const [historieLoading, setHistorieLoading] = useState(false);
  const [historieError, setHistorieError] = useState('');
  const [historieVan, setHistorieVan] = useState('');
  const [historieTot, setHistorieTot] = useState('');
  const [historieCoach, setHistorieCoach] = useState('');
  const [historieDetail, setHistorieDetail] = useState(null);

  const gemount = useRef(true);
  useEffect(() => () => { gemount.current = false; }, []);

  // ── Keuzelijsten laden ─────────────────────────────────────────
  useEffect(() => {
    let actief = true;

    async function laadLocaties() {
      const { data, error } = await supabase
        .from('locations').select('id, name').eq('is_active', true).order('name');
      if (!actief) return;
      if (error) {
        console.error('Locaties laden mislukt:', error);
        setOptieFouten(f => ({ ...f, locaties: t('broadcastPush.foutLocaties') }));
      } else {
        setLocations(data || []);
      }
    }

    async function laadAbonnementen() {
      const { data, error } = await supabase
        .from('membership_types').select('id, name').eq('is_active', true).order('name');
      if (!actief) return;
      if (error) {
        console.error('Abonnementstypen laden mislukt:', error);
        setOptieFouten(f => ({ ...f, abonnementen: t('broadcastPush.foutAbonnementen') }));
      } else {
        setMembershipTypes(data || []);
      }
    }

    async function laadCoaches() {
      if (!isAdminOrSuper) return;
      const { data, error } = await supabase
        .from('profiles').select('id, full_name')
        .in('role', ['coach', 'admin_coach', 'superadmin'])
        .is('deleted_at', null)
        .order('full_name');
      if (!actief) return;
      if (error) {
        console.error('Coaches laden mislukt:', error);
        setOptieFouten(f => ({ ...f, coaches: t('broadcastPush.foutCoaches') }));
      } else {
        setCoaches(data || []);
      }
    }

    laadLocaties();
    laadAbonnementen();
    laadCoaches();
    return () => { actief = false; };
  }, [isAdminOrSuper]);

  // ── Daglimiet ──────────────────────────────────────────────────
  const laadLimiet = useCallback(async () => {
    const { data, error } = await supabase.rpc('check_broadcast_rate_limit');
    if (!gemount.current) return;
    if (error) {
      console.error('Daglimiet ophalen mislukt:', error);
      setLimietFout(t('broadcastPush.foutLimiet'));
      setLimiet(null);
    } else {
      setLimietFout('');
      setLimiet(data);
    }
  }, []);

  useEffect(() => { laadLimiet(); }, [laadLimiet]);

  // ── Ontvangers ─────────────────────────────────────────────────
  const laadOntvangers = useCallback(async () => {
    setRecipientsLoading(true);
    setRecipientsError('');
    const { data, error } = await supabase.rpc('get_broadcast_recipients', {
      p_location_id: locationId || null,
      p_membership_type_id: membershipTypeId || null,
      p_coach_id: coachId || null,
    });
    if (!gemount.current) return;
    if (error) {
      console.error('Ontvangers laden mislukt:', error);
      setRecipientsError(t('broadcastPush.foutLaden'));
      setRecipients([]);
      setGeselecteerd(new Set());
    } else {
      const lijst = data || [];
      setRecipients(lijst);
      // Standaard staat elke bereikbare cliënt aangevinkt; onbereikbare
      // cliënten kunnen per definitie niet geselecteerd worden.
      setGeselecteerd(new Set(lijst.filter(r => r.reachable).map(r => r.client_id)));
    }
    setRecipientsLoading(false);
  }, [locationId, membershipTypeId, coachId]);

  useEffect(() => { laadOntvangers(); }, [laadOntvangers]);

  // ── Afgeleide tellingen ────────────────────────────────────────
  const tellingen = useMemo(() => {
    const totaal = recipients.length;
    const bereikbaar = recipients.filter(r => r.reachable).length;
    const geenToken = recipients.filter(r => r.status === 'no_token').length;
    const optOut = recipients.filter(r => r.status === 'opted_out').length;
    const gekozen = recipients.filter(r => r.reachable && geselecteerd.has(r.client_id)).length;
    return { totaal, bereikbaar, geenToken, optOut, gekozen, uitgesloten: bereikbaar - gekozen };
  }, [recipients, geselecteerd]);

  const zichtbareOntvangers = useMemo(() => {
    const term = zoek.trim().toLowerCase();
    if (!term) return recipients;
    return recipients.filter(r => (r.full_name || '').toLowerCase().includes(term));
  }, [recipients, zoek]);

  const zichtbaarBereikbaar = useMemo(
    () => zichtbareOntvangers.filter(r => r.reachable),
    [zichtbareOntvangers],
  );
  const allesZichtbaarGeselecteerd =
    zichtbaarBereikbaar.length > 0 && zichtbaarBereikbaar.every(r => geselecteerd.has(r.client_id));

  function wisselSelectie(clientId) {
    setGeselecteerd(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId); else next.add(clientId);
      return next;
    });
  }

  function wisselAlles() {
    setGeselecteerd(prev => {
      const next = new Set(prev);
      if (allesZichtbaarGeselecteerd) {
        zichtbaarBereikbaar.forEach(r => next.delete(r.client_id));
      } else {
        zichtbaarBereikbaar.forEach(r => next.add(r.client_id));
      }
      return next;
    });
  }

  // ── Validatie ──────────────────────────────────────────────────
  const validatie = useMemo(() => valideerBericht(title, body), [title, body]);
  const kanVersturen = validatie.geldig && tellingen.gekozen > 0 && !verzendBezig && !testBezig;
  const kanTesten = validatie.geldig && !verzendBezig && !testBezig;

  const huidigeFilters = useMemo(() => ({
    locationId: locationId || null,
    membershipTypeId: membershipTypeId || null,
    coachId: isAdminOrSuper ? (coachId || null) : null,
  }), [locationId, membershipTypeId, coachId, isAdminOrSuper]);

  // ── Verzenden ──────────────────────────────────────────────────
  function openBevestiging() {
    if (!kanVersturen) return;
    // Momentopname: wat de coach in de dialoog leest, is exact wat er wordt
    // ingestuurd — ook als de filters daarna nog wijzigen.
    setBevestiging({
      title: title.trim(),
      body: body.trim(),
      recipientIds: recipients.filter(r => r.reachable && geselecteerd.has(r.client_id)).map(r => r.client_id),
      filters: huidigeFilters,
      tellingen: { ...tellingen },
      idempotencyKey: maakIdempotencyKey(),
    });
  }

  async function bevestigVersturen() {
    if (!bevestiging || verzendBezig) return;
    setVerzendBezig(true);
    try {
      const res = await verstuurBroadcast(supabase, {
        mode: 'broadcast',
        title: bevestiging.title,
        body: bevestiging.body,
        recipientIds: bevestiging.recipientIds,
        filters: bevestiging.filters,
        idempotencyKey: bevestiging.idempotencyKey,
      });

      if (!res.ok) {
        showError(broadcastFoutTekst(res.error, res));
        return;
      }

      const regels = resultaatRegels(res.counts || {});
      setLaatsteResultaat({ ...res.counts, duplicaat: res.duplicaat });
      showSuccess(res.duplicaat ? t('broadcastPush.resultaatDuplicaat') : regels.join(' · '));

      setBevestiging(null);
      setTitle('');
      setBody('');
      await Promise.all([laadLimiet(), laadOntvangers()]);
      if (actieveTab === 'geschiedenis') laadHistorie(0);
    } catch (err) {
      console.error('Broadcast versturen mislukt:', err);
      showError(broadcastFoutTekst('onbekende_fout'));
    } finally {
      setVerzendBezig(false);
    }
  }

  async function verstuurTest() {
    if (!kanTesten) return;
    setTestBezig(true);
    try {
      const res = await verstuurBroadcast(supabase, {
        mode: 'test',
        title: title.trim(),
        body: body.trim(),
        idempotencyKey: maakIdempotencyKey(),
      });

      if (!res.ok) {
        showError(broadcastFoutTekst(res.error, res));
        return;
      }

      const c = res.counts || {};
      if ((c.accepted ?? 0) > 0) {
        showSuccess(t('broadcastPush.testGelukt'));
      } else if ((c.noToken ?? 0) > 0) {
        showError(t('broadcastPush.testGeenToken'));
      } else if ((c.optOut ?? 0) > 0) {
        showError(t('broadcastPush.testOptOut'));
      } else if ((c.rejected ?? 0) > 0) {
        showError(t('broadcastPush.testGeweigerd'));
      } else {
        showError(t('broadcastPush.testFout'));
      }
      if (actieveTab === 'geschiedenis') laadHistorie(historiePagina);
    } catch (err) {
      console.error('Testpush mislukt:', err);
      showError(t('broadcastPush.testFout'));
    } finally {
      setTestBezig(false);
    }
  }

  // ── Geschiedenis ───────────────────────────────────────────────
  const laadHistorie = useCallback(async (pagina) => {
    setHistorieLoading(true);
    setHistorieError('');
    const { data, error } = await supabase.rpc('get_push_broadcast_history', {
      p_from: historieVan ? new Date(`${historieVan}T00:00:00`).toISOString() : null,
      p_until: historieTot ? new Date(`${historieTot}T23:59:59.999`).toISOString() : null,
      p_coach_id: isAdminOrSuper ? (historieCoach || null) : null,
      p_limit: PAGINA_GROOTTE,
      p_offset: pagina * PAGINA_GROOTTE,
    });
    if (!gemount.current) return;
    if (error) {
      console.error('Geschiedenis laden mislukt:', error);
      setHistorieError(t('broadcastPush.foutHistorie'));
      setHistorie([]);
      setHistorieTotaal(0);
    } else {
      const rijen = data || [];
      setHistorie(rijen);
      setHistorieTotaal(rijen.length > 0 ? Number(rijen[0].total_count) : 0);
      setHistoriePagina(pagina);
    }
    setHistorieLoading(false);
  }, [historieVan, historieTot, historieCoach, isAdminOrSuper]);

  useEffect(() => {
    if (actieveTab === 'geschiedenis') laadHistorie(0);
  }, [actieveTab, laadHistorie]);

  const maxPagina = Math.max(Math.ceil(historieTotaal / PAGINA_GROOTTE) - 1, 0);

  // ── Rendering ──────────────────────────────────────────────────
  const tabStijl = (tab) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 18px',
    border: 'none',
    borderBottom: actieveTab === tab ? '3px solid var(--color-primary)' : '3px solid transparent',
    background: 'none',
    cursor: 'pointer',
    fontWeight: actieveTab === tab ? 700 : 400,
    color: actieveTab === tab ? 'var(--color-primary)' : 'var(--color-text-muted)',
    fontSize: 15,
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('broadcastPush.title')}</h1>
          <p className="page-subtitle">{t('broadcastPush.subtitle')}</p>
        </div>
      </div>

      <div
        role="tablist"
        aria-label={t('broadcastPush.title')}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 4,
          borderBottom: '1px solid var(--color-border)', marginBottom: 20,
        }}
      >
        <button
          type="button" role="tab" id="tab-nieuw" aria-controls="paneel-nieuw"
          aria-selected={actieveTab === 'nieuw'}
          style={tabStijl('nieuw')} onClick={() => setActieveTab('nieuw')}
        >
          <Send size={16} /> {t('broadcastPush.tabNieuw')}
        </button>
        <button
          type="button" role="tab" id="tab-geschiedenis" aria-controls="paneel-geschiedenis"
          aria-selected={actieveTab === 'geschiedenis'}
          style={tabStijl('geschiedenis')} onClick={() => setActieveTab('geschiedenis')}
        >
          <Bell size={16} /> {t('broadcastPush.tabGeschiedenis')}
        </button>
      </div>

      {actieveTab === 'nieuw' && (
        <div id="paneel-nieuw" role="tabpanel" aria-labelledby="tab-nieuw">
          <LimietBanner limiet={limiet} fout={limietFout} />

          <FilterKaart
            locations={locations} membershipTypes={membershipTypes} coaches={coaches}
            locationId={locationId} setLocationId={setLocationId}
            membershipTypeId={membershipTypeId} setMembershipTypeId={setMembershipTypeId}
            coachId={coachId} setCoachId={setCoachId}
            isAdminOrSuper={isAdminOrSuper} optieFouten={optieFouten}
          />

          <OntvangersKaart
            loading={recipientsLoading} fout={recipientsError}
            tellingen={tellingen} zoek={zoek} setZoek={setZoek}
            zichtbareOntvangers={zichtbareOntvangers}
            zichtbaarBereikbaar={zichtbaarBereikbaar}
            allesZichtbaarGeselecteerd={allesZichtbaarGeselecteerd}
            geselecteerd={geselecteerd}
            wisselSelectie={wisselSelectie} wisselAlles={wisselAlles}
            opnieuw={laadOntvangers}
          />

          <BerichtKaart
            title={title} setTitle={setTitle}
            body={body} setBody={setBody}
            validatie={validatie}
          />

          {laatsteResultaat && <ResultaatKaart counts={laatsteResultaat} />}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <button
              type="button" className="btn btn-primary"
              disabled={!kanVersturen} onClick={openBevestiging}
            >
              <Send size={16} />
              {tellingen.gekozen > 0
                ? t('broadcastPush.verstuurKnop').replace('{count}', tellingen.gekozen)
                : t('broadcastPush.verstuurKnopDisabled')}
            </button>
            <button
              type="button" className="btn btn-ghost"
              disabled={!kanTesten} onClick={verstuurTest}
            >
              <TestTube2 size={16} />
              {testBezig ? t('broadcastPush.testBezig') : t('broadcastPush.testKnop')}
            </button>
          </div>
        </div>
      )}

      {actieveTab === 'geschiedenis' && (
        <div id="paneel-geschiedenis" role="tabpanel" aria-labelledby="tab-geschiedenis">
          <GeschiedenisTab
            historie={historie} totaal={historieTotaal} pagina={historiePagina}
            maxPagina={maxPagina} loading={historieLoading} fout={historieError}
            van={historieVan} setVan={setHistorieVan}
            tot={historieTot} setTot={setHistorieTot}
            coachFilter={historieCoach} setCoachFilter={setHistorieCoach}
            coaches={coaches} isAdminOrSuper={isAdminOrSuper}
            laad={laadHistorie} openDetail={setHistorieDetail}
          />
        </div>
      )}

      {bevestiging && (
        <ConfirmModal
          title={t('broadcastPush.bevestigingTitel')}
          confirmLabel={verzendBezig ? t('broadcastPush.bevestigingBezig') : t('broadcastPush.bevestigingVersturen')}
          confirmDisabled={verzendBezig}
          onClose={() => { if (!verzendBezig) setBevestiging(null); }}
          onConfirm={bevestigVersturen}
          danger
          body={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, color: 'var(--color-text)' }}>
              <BevestigRegel label={t('broadcastPush.labelTitel')} waarde={bevestiging.title} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)' }}>
                  {t('broadcastPush.labelTekst')}
                </div>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{bevestiging.body}</div>
              </div>
              <BevestigRegel
                label={t('broadcastPush.bevestigingFilters')}
                waarde={filterSamenvatting(bevestiging.filters, locations, membershipTypes, coaches)}
              />
              <BevestigRegel
                label={t('broadcastPush.bevestigingGeselecteerd')}
                waarde={String(bevestiging.recipientIds.length)}
              />
              <BevestigRegel
                label={t('broadcastPush.bevestigingBereikbaar')}
                waarde={String(bevestiging.tellingen.gekozen)}
              />
              <BevestigRegel
                label={t('broadcastPush.bevestigingGeblokkeerd')}
                waarde={t('broadcastPush.bevestigingGeblokkeerdWaarde')
                  .replace('{geenToken}', bevestiging.tellingen.geenToken)
                  .replace('{optOut}', bevestiging.tellingen.optOut)}
              />
              <p style={{ margin: 0, color: '#b91c1c', fontWeight: 600 }}>
                {t('broadcastPush.bevestigingWaarschuwing')}
              </p>
            </div>
          }
        />
      )}

      {historieDetail && (
        <HistorieDetailModal
          rij={historieDetail}
          locations={locations} membershipTypes={membershipTypes} coaches={coaches}
          onClose={() => setHistorieDetail(null)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// Deelcomponenten
// ════════════════════════════════════════════════════════════════

function BevestigRegel({ label, waarde }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)' }}>{label}</div>
      <div style={{ wordBreak: 'break-word' }}>{waarde}</div>
    </div>
  );
}

function LimietBanner({ limiet, fout }) {
  if (fout) {
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ color: '#b91c1c' }}>{fout}</div>
      </div>
    );
  }
  if (!limiet) return null;

  const vol = limiet.allowed === false;
  return (
    <div
      className="card"
      style={{ marginBottom: 16, borderLeft: `4px solid ${vol ? '#dc2626' : 'var(--color-primary)'}` }}
    >
      <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'baseline' }}>
        <strong style={{ fontSize: 14 }}>
          {t('broadcastPush.limietGebruikt')
            .replace('{used}', limiet.used)
            .replace('{limit}', limiet.limit)}
        </strong>
        {limiet.resets_at && (
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            {t('broadcastPush.limietReset').replace('{moment}', formatMoment(limiet.resets_at))}
          </span>
        )}
      </div>
    </div>
  );
}

function FilterKaart({
  locations, membershipTypes, coaches,
  locationId, setLocationId, membershipTypeId, setMembershipTypeId,
  coachId, setCoachId, isAdminOrSuper, optieFouten,
}) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <h2 className="card-title">{t('broadcastPush.sectionFilters')}</h2>
      </div>
      <div
        className="card-body"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}
      >
        <div className="form-group">
          <label className="form-label" htmlFor="filter-locatie">{t('broadcastPush.filterLocatie')}</label>
          <select
            id="filter-locatie" className="form-input" value={locationId}
            onChange={e => setLocationId(e.target.value)}
          >
            <option value="">{t('broadcastPush.filterLocatiePlaceholder')}</option>
            {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
          </select>
          {optieFouten.locaties && <p style={{ color: '#b91c1c', fontSize: 12 }}>{optieFouten.locaties}</p>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="filter-abonnement">{t('broadcastPush.filterAbonnementstype')}</label>
          <select
            id="filter-abonnement" className="form-input" value={membershipTypeId}
            onChange={e => setMembershipTypeId(e.target.value)}
          >
            <option value="">{t('broadcastPush.filterAbonnementstypePlaceholder')}</option>
            {membershipTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
          </select>
          {optieFouten.abonnementen && <p style={{ color: '#b91c1c', fontSize: 12 }}>{optieFouten.abonnementen}</p>}
        </div>

        {isAdminOrSuper && (
          <div className="form-group">
            <label className="form-label" htmlFor="filter-coach">{t('broadcastPush.filterCoach')}</label>
            <select
              id="filter-coach" className="form-input" value={coachId}
              onChange={e => setCoachId(e.target.value)}
            >
              <option value="">{t('broadcastPush.filterCoachPlaceholder')}</option>
              {coaches.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </select>
            {optieFouten.coaches && <p style={{ color: '#b91c1c', fontSize: 12 }}>{optieFouten.coaches}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const kaart = {
    reachable: { klasse: 'badge-paid', label: t('broadcastPush.statusBereikbaar') },
    no_token: { klasse: 'badge-inactive', label: t('broadcastPush.statusGeenToken') },
    opted_out: { klasse: 'badge-reminded', label: t('broadcastPush.statusOptOut') },
  }[status] || { klasse: 'badge-inactive', label: status };

  return <span className={`badge ${kaart.klasse}`}>{kaart.label}</span>;
}

function OntvangersKaart({
  loading, fout, tellingen, zoek, setZoek,
  zichtbareOntvangers, zichtbaarBereikbaar, allesZichtbaarGeselecteerd,
  geselecteerd, wisselSelectie, wisselAlles, opnieuw,
}) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <h2 className="card-title">{t('broadcastPush.previewSection')}</h2>
      </div>
      <div className="card-body">
        {loading ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                aria-hidden="true"
                style={{ height: 34, borderRadius: 8, background: 'var(--color-bg)' }}
              />
            ))}
            <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
              {t('broadcastPush.previewLoading')}
            </p>
          </div>
        ) : fout ? (
          <div>
            <p style={{ color: '#b91c1c', marginBottom: 12 }}>{fout}</p>
            <button type="button" className="btn btn-ghost" onClick={opnieuw}>
              {t('broadcastPush.opnieuwProberen')}
            </button>
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 10, marginBottom: 16,
              }}
            >
              <Teller label={t('broadcastPush.tellerTotaal')} waarde={tellingen.totaal} />
              <Teller label={t('broadcastPush.tellerBereikbaar')} waarde={tellingen.bereikbaar} accent />
              <Teller label={t('broadcastPush.tellerGeenToken')} waarde={tellingen.geenToken} />
              <Teller label={t('broadcastPush.tellerOptOut')} waarde={tellingen.optOut} />
              <Teller label={t('broadcastPush.tellerUitgesloten')} waarde={tellingen.uitgesloten} />
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
              <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 0 }}>
                <Search
                  size={15}
                  style={{
                    position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--color-text-muted)', pointerEvents: 'none',
                  }}
                />
                <input
                  type="search" className="form-input" style={{ paddingLeft: 32 }}
                  placeholder={t('broadcastPush.zoekPlaceholder')}
                  aria-label={t('broadcastPush.zoekPlaceholder')}
                  value={zoek} onChange={e => setZoek(e.target.value)}
                />
              </div>
              <button
                type="button" className="btn btn-ghost"
                onClick={wisselAlles} disabled={zichtbaarBereikbaar.length === 0}
              >
                {allesZichtbaarGeselecteerd
                  ? t('broadcastPush.deselecteerAlles')
                  : t('broadcastPush.selecteerAlles')}
              </button>
            </div>

            <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
              {t('broadcastPush.selectieTeller')
                .replace('{selected}', tellingen.gekozen)
                .replace('{reachable}', tellingen.bereikbaar)}
            </p>

            {zichtbareOntvangers.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)' }}>
                {t('broadcastPush.previewGeenOntvangers')}
              </p>
            ) : (
              <ul
                style={{
                  listStyle: 'none', margin: 0, padding: 8, maxHeight: 320, overflowY: 'auto',
                  border: '1px solid var(--color-border)', borderRadius: 8,
                }}
              >
                {zichtbareOntvangers.map(r => (
                  <li key={r.client_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <label
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        padding: '8px 6px',
                        cursor: r.reachable ? 'pointer' : 'not-allowed',
                        opacity: r.reachable ? 1 : 0.6,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={r.reachable && geselecteerd.has(r.client_id)}
                        disabled={!r.reachable}
                        onChange={() => wisselSelectie(r.client_id)}
                      />
                      <span style={{ fontSize: 14, flex: '1 1 120px', minWidth: 0, wordBreak: 'break-word' }}>
                        {r.full_name}
                      </span>
                      <StatusChip status={r.status} />
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Teller({ label, waarde, accent = false }) {
  return (
    <div style={{ background: 'var(--color-bg)', borderRadius: 8, padding: '8px 10px' }}>
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 700, color: accent ? 'var(--color-primary)' : 'var(--color-text)' }}>
        {waarde}
      </div>
    </div>
  );
}

function TekenTeller({ lengte, max, fout, waarschuwing }) {
  const kleur = fout ? '#b91c1c' : waarschuwing ? '#c2410c' : 'var(--color-text-muted)';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, color: kleur }}>
        {fout === 'te_lang' && t('broadcastPush.validatieTeLang').replace('{max}', max)}
        {waarschuwing && !fout && t('broadcastPush.validatieAfkapWaarschuwing')}
      </span>
      <span style={{ fontSize: 12, color: kleur }}>{lengte} / {max}</span>
    </div>
  );
}

function BerichtKaart({ title, setTitle, body, setBody, validatie }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <h2 className="card-title">{t('broadcastPush.sectionBericht')}</h2>
      </div>
      <div
        className="card-body"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <div className="form-group">
            <label className="form-label" htmlFor="push-titel">{t('broadcastPush.labelTitel')}</label>
            <input
              id="push-titel" type="text" className="form-input"
              placeholder={t('broadcastPush.labelTitelPlaceholder')}
              value={title} onChange={e => setTitle(e.target.value)}
              aria-describedby="push-titel-teller"
            />
            <div id="push-titel-teller">
              <TekenTeller
                lengte={validatie.titelLengte} max={TITEL_MAX}
                fout={validatie.titelFout === 'te_lang' ? 'te_lang' : null}
                waarschuwing={validatie.titelWaarschuwing}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="push-tekst">{t('broadcastPush.labelTekst')}</label>
            <textarea
              id="push-tekst" className="form-input" rows={6}
              placeholder={t('broadcastPush.labelTekstPlaceholder')}
              value={body} onChange={e => setBody(e.target.value)}
              aria-describedby="push-tekst-teller"
            />
            <div id="push-tekst-teller">
              <TekenTeller
                lengte={validatie.tekstLengte} max={TEKST_MAX}
                fout={validatie.tekstFout === 'te_lang' ? 'te_lang' : null}
                waarschuwing={validatie.tekstWaarschuwing}
              />
            </div>
          </div>
        </div>

        <TelefoonPreview title={title} body={body} />
      </div>
    </div>
  );
}

function TelefoonPreview({ title, body }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 8 }}>
        {t('broadcastPush.previewTelefoon')}
      </div>
      <div
        style={{
          background: 'linear-gradient(160deg,#1f2937,#111827)',
          borderRadius: 20, padding: 16, maxWidth: 320,
        }}
      >
        <div
          style={{
            background: 'rgba(255,255,255,0.94)', borderRadius: 14, padding: '10px 12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span
              style={{
                width: 16, height: 16, borderRadius: 4, background: 'var(--color-primary)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Bell size={10} color="#fff" />
            </span>
            <span style={{ fontSize: 11, color: '#4b5563', fontWeight: 600 }}>
              {t('broadcastPush.previewAppNaam')}
            </span>
            <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>
              {t('broadcastPush.previewNu')}
            </span>
          </div>
          <div
            style={{
              fontSize: 14, fontWeight: 700, color: '#111827',
              overflowWrap: 'anywhere', display: '-webkit-box',
              WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}
          >
            {title.trim() || t('broadcastPush.previewLegeTitel')}
          </div>
          <div
            style={{
              fontSize: 13, color: '#374151', marginTop: 2,
              overflowWrap: 'anywhere', whiteSpace: 'pre-wrap',
              display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}
          >
            {body.trim() || t('broadcastPush.previewLegeTekst')}
          </div>
        </div>
      </div>
      <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 8, maxWidth: 320 }}>
        {t('broadcastPush.previewToelichting')}
      </p>
    </div>
  );
}

function ResultaatKaart({ counts }) {
  const regels = resultaatRegels(counts);
  return (
    <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--color-primary)' }}>
      <div className="card-header">
        <h2 className="card-title">{t('broadcastPush.resultaatTitel')}</h2>
      </div>
      <div className="card-body">
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {regels.map(r => <li key={r} style={{ fontSize: 14 }}>{r}</li>)}
        </ul>
      </div>
    </div>
  );
}

function TypeBadge({ type }) {
  const kaart = {
    broadcast: { klasse: 'badge-sent', label: t('broadcastPush.typeBroadcast') },
    test: { klasse: 'badge-draft', label: t('broadcastPush.typeTest') },
    selective: { klasse: 'badge-active', label: t('broadcastPush.typeSelectief') },
  }[type] || { klasse: 'badge-draft', label: type };
  return <span className={`badge ${kaart.klasse}`}>{kaart.label}</span>;
}

function StatusBadgePush({ status }) {
  const kaart = {
    sent: { klasse: 'badge-paid', label: t('broadcastPush.statusVerzonden') },
    pending: { klasse: 'badge-reminded', label: t('broadcastPush.statusBezig') },
    failed: { klasse: 'badge-final-notice', label: t('broadcastPush.statusMislukt') },
  }[status] || { klasse: 'badge-draft', label: status };
  return <span className={`badge ${kaart.klasse}`}>{kaart.label}</span>;
}

function GeschiedenisTab({
  historie, totaal, pagina, maxPagina, loading, fout,
  van, setVan, tot, setTot, coachFilter, setCoachFilter,
  coaches, isAdminOrSuper, laad, openDetail,
}) {
  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <h2 className="card-title">{t('broadcastPush.historieFilters')}</h2>
        </div>
        <div
          className="card-body"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}
        >
          <div className="form-group">
            <label className="form-label" htmlFor="hist-van">{t('broadcastPush.historieVan')}</label>
            <input
              id="hist-van" type="date" className="form-input"
              value={van} onChange={e => setVan(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="hist-tot">{t('broadcastPush.historieTot')}</label>
            <input
              id="hist-tot" type="date" className="form-input"
              value={tot} onChange={e => setTot(e.target.value)}
            />
          </div>
          {isAdminOrSuper && (
            <div className="form-group">
              <label className="form-label" htmlFor="hist-coach">{t('broadcastPush.historieCoach')}</label>
              <select
                id="hist-coach" className="form-input"
                value={coachFilter} onChange={e => setCoachFilter(e.target.value)}
              >
                <option value="">{t('broadcastPush.historieCoachAlle')}</option>
                {coaches.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">{t('broadcastPush.tabGeschiedenis')}</h2>
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            {t('broadcastPush.historieTotaal').replace('{count}', totaal)}
          </span>
        </div>
        <div className="card-body">
          {loading ? (
            <div style={{ display: 'grid', gap: 8 }}>
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} aria-hidden="true" style={{ height: 38, borderRadius: 8, background: 'var(--color-bg)' }} />
              ))}
              <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{t('broadcastPush.historieLaden')}</p>
            </div>
          ) : fout ? (
            <div>
              <p style={{ color: '#b91c1c', marginBottom: 12 }}>{fout}</p>
              <button type="button" className="btn btn-ghost" onClick={() => laad(0)}>
                {t('broadcastPush.opnieuwProberen')}
              </button>
            </div>
          ) : historie.length === 0 ? (
            <div className="empty-state">
              <strong>{t('broadcastPush.historieLeeg')}</strong>
              <p>{t('broadcastPush.historieLeegUitleg')}</p>
            </div>
          ) : (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('broadcastPush.kolomMoment')}</th>
                      <th>{t('broadcastPush.kolomCoach')}</th>
                      <th>{t('broadcastPush.kolomType')}</th>
                      <th>{t('broadcastPush.kolomTitel')}</th>
                      <th>{t('broadcastPush.kolomResultaat')}</th>
                      <th aria-label={t('broadcastPush.kolomActie')}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {historie.map(rij => (
                      <tr key={rij.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatMoment(rij.sent_at)}</td>
                        <td>{rij.coach_name || '—'}</td>
                        <td><TypeBadge type={rij.type} /></td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{rij.title}</div>
                          <div
                            style={{
                              fontSize: 12, color: 'var(--color-text-muted)',
                              maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                          >
                            {rij.body}
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <StatusBadgePush status={rij.status} />
                            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                              {t('broadcastPush.historieKort')
                                .replace('{accepted}', rij.success_count)
                                .replace('{selected}', rij.selected_count)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <button type="button" className="btn btn-ghost" onClick={() => openDetail(rij)}>
                            {t('broadcastPush.detailKnop')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: 12, flexWrap: 'wrap', marginTop: 16,
                }}
              >
                <button
                  type="button" className="btn btn-ghost"
                  disabled={pagina <= 0} onClick={() => laad(pagina - 1)}
                >
                  {t('broadcastPush.paginaVorige')}
                </button>
                <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                  {t('broadcastPush.paginaVan')
                    .replace('{pagina}', pagina + 1)
                    .replace('{totaal}', maxPagina + 1)}
                </span>
                <button
                  type="button" className="btn btn-ghost"
                  disabled={pagina >= maxPagina} onClick={() => laad(pagina + 1)}
                >
                  {t('broadcastPush.paginaVolgende')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function HistorieDetailModal({ rij, locations, membershipTypes, coaches, onClose }) {
  const filters = {
    locationId: rij.filters?.location_id || null,
    membershipTypeId: rij.filters?.membership_type_id || null,
    coachId: rij.filters?.coach_id || null,
  };

  return (
    <ConfirmModal
      title={t('broadcastPush.detailTitel')}
      confirmLabel={t('broadcastPush.detailSluiten')}
      onConfirm={onClose}
      onClose={onClose}
      body={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, color: 'var(--color-text)' }}>
          <BevestigRegel label={t('broadcastPush.kolomMoment')} waarde={formatMoment(rij.sent_at)} />
          <BevestigRegel label={t('broadcastPush.kolomCoach')} waarde={rij.coach_name || '—'} />
          <BevestigRegel label={t('broadcastPush.labelTitel')} waarde={rij.title} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)' }}>
              {t('broadcastPush.labelTekst')}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{rij.body}</div>
          </div>
          <BevestigRegel
            label={t('broadcastPush.bevestigingFilters')}
            waarde={rij.type === 'broadcast'
              ? filterSamenvatting(filters, locations, membershipTypes, coaches)
              : t('broadcastPush.filterNvt')}
          />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 4 }}>
              {t('broadcastPush.kolomResultaat')}
            </div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>{t('broadcastPush.detailGeselecteerd').replace('{count}', rij.selected_count)}</li>
              <li>{t('broadcastPush.resultaatGeaccepteerd').replace('{count}', rij.success_count)}</li>
              <li>{t('broadcastPush.resultaatGeenToken').replace('{count}', rij.no_token_count)}</li>
              <li>{t('broadcastPush.resultaatOptOut').replace('{count}', rij.opt_out_count)}</li>
              <li>{t('broadcastPush.resultaatGeweigerd').replace('{count}', rij.rejected_count)}</li>
              <li>{t('broadcastPush.resultaatFout').replace('{count}', rij.error_count)}</li>
            </ul>
          </div>
        </div>
      }
    />
  );
}
